import { useMount } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import { useState } from 'react';
import { sound } from './sound.js';
import { isRunning, resume } from './utils.js';
import { logger } from '#~/logger/index.js';

/**
 * Hook to determine if the global audio context is running (as opposed to being "suspended").
 *
 * @returns `true` if the audio context is running, `false` otherwise.
 */
export const useAudioRunning = (): boolean => {
  const { context } = sound;

  const [state, setState] = useState<boolean>(isRunning(context));

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Watch for changes to the audio context state.
    context.addEventListener(
      'statechange',
      () => {
        setState(isRunning(context));
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });

  return state;
};

/**
 * Hook to resume the audio context in response to a user interaction.
 *
 * @see https://bit.ly/3JXOxy9
 */
export const useResumeAudio = (): void => {
  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    document.addEventListener(
      'click',
      () => {
        resume(sound.context)
          .then((running) => {
            if (running) {
              logger.debug('Audio context running.');

              // Can remove the listener if the audio context has been resumed.
              abortController.abort();
            }
          })
          .catch(noop);
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });
};
